import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRouter } from "next/router";
import * as yup from "yup";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Link as MuiLink,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Save as SaveIcon } from "@material-ui/icons";
import { Alert, Skeleton } from "@material-ui/lab";
import { SubHeader } from "src/apps/commons/dataDisplays";
import { SimpleSubTable } from "src/apps/commons/form";
import { useListSubFormState } from "src/apps/commons/form/hooks";
import { toDropdown } from "src/utils";
import { formatDateTime, parseToOffsetDateTime } from "src/utils/dateUtils";
import { yupTrimToNull } from "src/utils/stringUtils";
import useHttpErrorHandler from "../../../../hooks/useHttpErrorHandler";
import { ImageUpload } from "../../../commons/fileUploads";
import { IRemyndBrandBrief } from "../../remyndBrand/types";
import { IRemyndProduct } from "../../remyndProduct/types";
import { listConflict } from "../api";
import {
  IRemyndWarranty,
  IRemyndWarrantyBrandMapSaveReqTemp,
  IRemyndWarrantyConflictList,
  IRemyndWarrantyContainer,
  IRemyndWarrantyDetail,
  IRemyndWarrantyDetailSaveReq,
  IRemyndWarrantyProductMapSaveReqTemp,
  IRemyndWarrantySaveReq,
  IRemyndWarrantySaveReqContainer
} from "../types";
import { paths } from "../utils";
import WarrantyBrandCatModal from "./WarrantyBrandCatModal";
import WarrantyDetailModal from "./WarrantyDetailModal";
import WarrantyProductModal from "./WarrantyProductModal";
import { warrantyTypeMap } from "src/utils/enum";

const useStyles = makeStyles({
  button: {
    paddingTop: "16px"
  },
  layoutButton: {
    flex: "1 1 auto"
  },
  skeleton: {
    display: "inline-flex",
    height: "72px",
    width: "100%"
  }
});

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  data?: IRemyndWarrantyContainer;
  onSubmit: (req: IRemyndWarrantySaveReqContainer) => void;
}

const schema = yup.object().shape({
  titleTh: yup
    .string()
    .transform(yupTrimToNull)
    .required("กรุณาระบุ ชื่อ (TH)"),
  titleEn: yup
    .string()
    .transform(yupTrimToNull)
    .required("กรุณาระบุ ชื่อ (EN)"),
  remarkEn: yup.string().transform(yupTrimToNull),
  remarkTh: yup.string().transform(yupTrimToNull),
  imageId: yup.string().transform(yupTrimToNull),
  active: yup.boolean()
});

type FormData = IRemyndWarrantySaveReq & {
  brandCatMaps: IRemyndWarrantyBrandMapSaveReqTemp[];
  productMaps: IRemyndWarrantyProductMapSaveReqTemp[];
  details: IRemyndWarrantyDetailSaveReq[];
};

// BrandCat
type WarrantyBrandCatModalState = {
  open: boolean;
  index?: number;
  data?: IRemyndWarrantyBrandMapSaveReqTemp;
  brandBrief?: IRemyndBrandBrief;
};

// Detail
type WarrantyDetailModalState = {
  open: boolean;
  index?: number;
  data?: IRemyndWarrantyDetailSaveReq;
  detail?: IRemyndWarrantyDetail;
};

// Product
type WarrantyProductModalState = {
  open: boolean;
  index?: number;
  data?: IRemyndWarrantyProductMapSaveReqTemp;
  product?: IRemyndProduct;
};

const WarrantyMappingConflictList = (props: {
  title: string;
  list: IRemyndWarranty[];
}) => {
  const { title, list } = props;
  return (
    <Alert severity="warning">
      {title}
      <ul style={{ margin: "0.25rem 0" }}>
        {list.map(w => {
          const desc = `(แก้ไขล่าสุดเมื่อ ${formatDateTime(w.updatedAt)})`;
          return (
            <li key={w.id}>
              <Link passHref href={`${paths.edit}?id=${w.id}`}>
                <MuiLink target="_blank">
                  <strong>{w.titleTh}</strong>
                </MuiLink>
              </Link>{" "}
              <Typography variant="caption">{desc}</Typography>
            </li>
          );
        })}
      </ul>
    </Alert>
  );
};

const Form: React.FC<Props> = props => {
  const { isLoading, isSaving, data, onSubmit } = props;
  const classes = useStyles();
  const router = useRouter();
  const { handleError } = useHttpErrorHandler();

  const defaultFormData = useMemo<Partial<FormData>>(
    () => ({
      titleTh: data?.warranty.titleTh ?? "",
      titleEn: data?.warranty.titleEn ?? "",
      remarkTh: data?.warranty.remarkTh ?? "",
      remarkEn: data?.warranty.remarkEn ?? "",
      imageId: data?.warranty.imageId ?? "",
      active: data?.warranty.active ?? true,

      brandCatMaps:
        data?.brandMaps.map(c => ({
          brand: toDropdown(c.brand),
          categoryLv1: c?.categoryLv1 ? toDropdown(c.categoryLv1) : undefined,
          categoryLv2: c?.categoryLv2 ? toDropdown(c.categoryLv2) : undefined,
          categoryLv3: c?.categoryLv3 ? toDropdown(c.categoryLv3) : undefined
        })) ?? [],

      productMaps:
        data?.productMaps.map(c => ({
          product: toDropdown(c.product)
        })) ?? [],
      details:
        data?.details.map(c => ({
          ...c
        })) ?? []
    }),
    [data]
  );
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    clearErrors,
    reset
  } = useForm<FormData>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  // BrandCat
  const {
    addItem: addBrandCatMap,
    setItem: setBrandCatMap,
    delItem: delBrandCatMap,
    items: brandCatMaps
  } = useListSubFormState<IRemyndWarrantyBrandMapSaveReqTemp>(
    defaultFormData.brandCatMaps
  );
  const [brandCatModal, setBrandCatModal] =
    useState<WarrantyBrandCatModalState>({
      open: false
    });
  const handleCloseBrandCatModal = () => {
    setBrandCatModal({ open: false, index: undefined, data: undefined });
  };
  const handleSubmitBrandCatModal = (
    req: IRemyndWarrantyBrandMapSaveReqTemp
  ) => {
    if (brandCatModal.index !== undefined) {
      setBrandCatMap(req, brandCatModal.index);
    } else {
      addBrandCatMap(req);
    }
    handleCloseBrandCatModal();
  };

  // Product
  const {
    addItem: addProductMap,
    setItem: setProductMap,
    delItem: delProductMap,
    items: productMaps
  } = useListSubFormState<IRemyndWarrantyProductMapSaveReqTemp>(
    defaultFormData.productMaps
  );
  const [productModal, setProductModal] = useState<WarrantyProductModalState>({
    open: false
  });
  const handleCloseProductModal = () => {
    setProductModal({ open: false, index: undefined, data: undefined });
  };
  const handleSubmitProductModal = (
    req: IRemyndWarrantyProductMapSaveReqTemp
  ) => {
    if (productModal.index !== undefined) {
      setProductMap(req, productModal.index);
    } else {
      addProductMap(req);
    }
    handleCloseProductModal();
  };

  // Detail
  const {
    addItem: addDetail,
    setItem: setDetail,
    delItem: delDetail,
    moveItem: moveDetail,
    items: details
  } = useListSubFormState<IRemyndWarrantyDetailSaveReq>(
    defaultFormData.details
  );
  const [detailModal, setDetailModal] = useState<WarrantyDetailModalState>({
    open: false
  });
  const handleCloseDetailModal = () => {
    setDetailModal({ open: false, index: undefined, data: undefined });
  };
  const handleSubmitDetailModal = (req: IRemyndWarrantyDetailSaveReq) => {
    if (detailModal.index !== undefined) {
      setDetail(req, detailModal.index);
    } else {
      addDetail(req);
    }
    handleCloseDetailModal();
    clearErrors("details");
  };
  const handleMoveDetailUp = (idx: number) =>
    idx > 0 ? moveDetail(idx, idx - 1) : undefined;
  const handleMoveDetailDown = (idx: number) =>
    idx < details.length - 1 ? moveDetail(idx, idx + 1) : undefined;

  const handleTransformAndSubmit = (form: FormData) => {
    // Custom validation for data outside of form state
    if (details.length === 0) {
      setError("details", {
        message: "กรุณาระบุรายละเอียดการรับประกัน อย่างน้อย 1 รายการ"
      });
      return;
    }

    onSubmit({
      warranty: {
        titleTh: form.titleTh,
        titleEn: form.titleEn,
        remarkTh: form.remarkTh,
        remarkEn: form.remarkEn,
        imageId: form.imageId,
        active: form.active
      },
      brandMaps: brandCatMaps.map(it => ({
        brandId: it.brand.value,
        categoryLv1Id: it.categoryLv1?.value,
        categoryLv2Id: it.categoryLv2?.value,
        categoryLv3Id: it.categoryLv3?.value
      })),
      productMaps: productMaps.map(it => ({
        productId: it.product?.value
      })),
      details: details.map((it, idx) => ({
        id: it.id,
        ord: idx,
        labelTh: it.labelTh,
        labelEn: it.labelEn,
        type: it.type,
        years: it.years,
        months: it.months,
        days: it.days,
        remarkTh: it.remarkTh,
        remarkEn: it.remarkEn,
        effectiveFrom: it.effectiveFrom
          ? parseToOffsetDateTime(it.effectiveFrom)
          : undefined,
        effectiveUntil: it.effectiveUntil
          ? parseToOffsetDateTime(it.effectiveUntil)
          : undefined
      }))
    });
  };

  useEffect(() => {
    if (data) {
      reset(defaultFormData);
    }
  }, [reset, data, defaultFormData]);

  // ---- Check Conflicts ----------------------------------------------------

  const [conflictState, setConflictState] =
    useState<IRemyndWarrantyConflictList>({
      brandMapConflicts: [],
      productMapConflicts: []
    });

  const fetchConflictsByBrandMaps = useCallback(async () => {
    if (brandCatMaps.length === 0) {
      setConflictState(prevState => ({
        ...prevState,
        brandMapConflicts: []
      }));
      return;
    }
    try {
      const response = await listConflict({
        warrantyId: data?.warranty?.id,
        active: true,
        brandMaps: brandCatMaps.map(it => ({
          brandId: it.brand.value,
          categoryLv1Id: it.categoryLv1?.value,
          categoryLv2Id: it.categoryLv2?.value,
          categoryLv3Id: it.categoryLv3?.value
        })),
        productMaps: []
      });
      setConflictState(prevState => ({
        ...prevState,
        brandMapConflicts: response.value.brandMapConflicts
      }));
    } catch (e) {
      handleError(e);
    }
  }, [data?.warranty?.id, handleError, brandCatMaps]);

  const fetchConflictsByProductMaps = useCallback(async () => {
    if (productMaps.length === 0) {
      setConflictState(prevState => ({
        ...prevState,
        productMapConflicts: []
      }));
      return;
    }
    try {
      const response = await listConflict({
        warrantyId: data?.warranty?.id,
        active: true,
        brandMaps: [],
        productMaps: productMaps.map(it => ({
          productId: it.product?.value
        }))
      });
      setConflictState(prevState => ({
        ...prevState,
        productMapConflicts: response.value.productMapConflicts
      }));
    } catch (e) {
      handleError(e);
    }
  }, [data?.warranty?.id, handleError, productMaps]);

  useEffect(() => {
    fetchConflictsByBrandMaps();
  }, [fetchConflictsByBrandMaps]);
  useEffect(() => {
    fetchConflictsByProductMaps();
  }, [fetchConflictsByProductMaps]);

  const { brandMapConflicts, productMapConflicts } = conflictState;

  const brandMapSubRowRenderer = useCallback(
    (req: IRemyndWarrantyBrandMapSaveReqTemp) => {
      const conflicts = brandMapConflicts.find(
        c =>
          (c.key.categoryLv3Id ?? "") === (req.categoryLv3?.value ?? "") &&
          (c.key.categoryLv2Id ?? "") === (req.categoryLv2?.value ?? "") &&
          (c.key.categoryLv1Id ?? "") === (req.categoryLv1?.value ?? "") &&
          c.key.brandId === req.brand.value
      );
      if (!conflicts || conflicts.warrantyList.length === 0) return undefined;
      return (
        <WarrantyMappingConflictList
          title={
            req.categoryLv1?.value
              ? "มีข้อมูลการรับประกันอื่น ที่ Active และผูกกับแบรนด์และหมวดสินค้านี้เหมือนกัน"
              : "มีข้อมูลการรับประกันอื่น ที่ Active และผูกกับแบรนด์นี้เหมือนกัน"
          }
          list={conflicts.warrantyList}
        />
      );
    },
    [brandMapConflicts]
  );
  const productMapSubRowRenderer = useCallback(
    (req: IRemyndWarrantyProductMapSaveReqTemp) => {
      const conflicts = productMapConflicts.find(
        c => c.key.productId === req.product.value
      );
      if (!conflicts || conflicts.warrantyList.length === 0) return undefined;
      return (
        <WarrantyMappingConflictList
          title="มีข้อมูลการรับประกันอื่น ที่ Active และผูกกับสินค้านี้เหมือนกัน"
          list={conflicts.warrantyList}
        />
      );
    },
    [productMapConflicts]
  );

  // -------------------------------------------------------------------------

  const ProductSubTableCell = useCallback(
    (req: IRemyndWarrantyProductMapSaveReqTemp) => {
      const { meta: brief } = req.product;
      return (
        <div>
          {brief.label}
          <br />
          <Typography variant="caption">
            {brief.brand?.label ?? "ไม่ระบุแบรนด์"}
            {" / "}
            {[
              brief.categoryLv1?.label ?? "",
              brief.categoryLv2?.label ?? "",
              brief.categoryLv3?.label ?? ""
            ].join(" ")}
          </Typography>
        </div>
      );
    },
    []
  );

  return (
    <>
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <Grid container alignItems="flex-start" spacing={2}>
          <Grid item xs={12} md={6} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="titleTh"
                control={control}
                defaultValue={defaultFormData.titleTh}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    error={!!errors.titleTh}
                    helperText={errors.titleTh?.message}
                    {...field}
                    label="ชื่อ (TH) *"
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="titleEn"
                control={control}
                defaultValue={defaultFormData.titleEn}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    error={!!errors.titleEn}
                    helperText={errors.titleEn?.message}
                    {...field}
                    label="ชื่อ (EN) *"
                  />
                )}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="remarkTh"
                control={control}
                defaultValue={defaultFormData.remarkTh}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="หมายเหตุ (TH)"
                    multiline
                    rows={2}
                    rowsMax={16}
                  />
                )}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <Controller
                name="remarkEn"
                control={control}
                defaultValue={defaultFormData.remarkEn}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled={isSaving}
                    {...field}
                    label="หมายเหตุ (EN)"
                    multiline
                    rows={2}
                    rowsMax={16}
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="imageId"
              control={control}
              defaultValue={defaultFormData.imageId ?? ""}
              render={({ field: { value, onChange } }) => (
                <ImageUpload
                  fileId={value}
                  label={"รูปประกอบ"}
                  onChange={onChange}
                  disabled={isSaving}
                />
              )}
            />
          </Grid>

          <Grid item>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Controller
                      name="active"
                      control={control}
                      defaultValue={defaultFormData.active}
                      render={({ field: { value, onChange } }) => (
                        <Box ml={1}>
                          <Checkbox
                            checked={value}
                            onChange={onChange}
                            disabled={isSaving}
                            color="primary"
                          />
                        </Box>
                      )}
                    />
                  }
                  label="Active"
                />
              </FormGroup>
            )}
          </Grid>

          <Grid item xs={12}>
            <SubHeader
              title={`รายละเอียดการรับประกัน (${details.length})`}
              marginTop
            />
            <ErrorMessage
              errors={errors}
              name="details"
              render={({ message }) => (
                <Typography variant="overline" color="error" gutterBottom>
                  {message}
                </Typography>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <SimpleSubTable<IRemyndWarrantyDetailSaveReq>
              data={details}
              onClickAdd={() => setDetailModal({ open: true })}
              onClickEdit={(req, idx) =>
                setDetailModal({
                  open: true,
                  index: idx,
                  data: req
                })
              }
              onClickDelete={(_, idx) => delDetail(idx)}
              onMoveUp={handleMoveDetailUp}
              onMoveDown={handleMoveDetailDown}
              showIndex
              columns={[
                {
                  label: "ชื่อ (TH)",
                  minWidth: 140,
                  renderCell: req => req.labelTh
                },
                {
                  label: "ชื่อ (EN)",
                  minWidth: 140,
                  renderCell: req => req.labelEn
                },
                {
                  label: "หมายเหตุ (TH)",
                  minWidth: 160,
                  renderCell: req => req.remarkTh
                },
                {
                  label: "หมายเหตุ (EN)",
                  minWidth: 160,
                  renderCell: req => req.remarkEn
                },
                {
                  label: "ประเภทการรับประกัน",
                  width: 180,
                  renderCell: row => warrantyTypeMap[row.type]
                },
                {
                  label: "รับประกัน (ปี)",
                  width: 100,
                  align: "center",
                  renderCell: row => row.years
                },
                {
                  label: "รับประกัน (เดือน)",
                  width: 100,
                  align: "center",
                  renderCell: row => row.months
                },
                {
                  label: "รับประกัน (วัน)",
                  width: 100,
                  align: "center",
                  renderCell: row => row.days
                },

                {
                  label: "Effective Date",
                  width: 180,
                  renderCell: req =>
                    !req.effectiveFrom && !req.effectiveUntil
                      ? ""
                      : `${
                          req.effectiveFrom
                            ? formatDateTime(req.effectiveFrom)
                            : "ไม่ระบุ"
                        } - ${
                          req.effectiveUntil
                            ? formatDateTime(req.effectiveUntil)
                            : "ไม่ระบุ"
                        }`
                }
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <SubHeader
              title={`ผูกกับแบรนด์และหมวดสินค้า (${brandCatMaps.length})`}
              marginTop
            />
          </Grid>
          <Grid item xs={12}>
            <SimpleSubTable<IRemyndWarrantyBrandMapSaveReqTemp>
              data={brandCatMaps}
              onClickAdd={() => setBrandCatModal({ open: true })}
              onClickEdit={(req, idx) =>
                setBrandCatModal({
                  open: true,
                  index: idx,
                  data: req
                })
              }
              onClickDelete={(_, idx) => delBrandCatMap(idx)}
              showIndex={false}
              columns={[
                {
                  label: "แบรนด์",
                  minWidth: 180,
                  renderCell: req => req.brand.label
                },
                {
                  label: "หมวดสินค้า Lv1",
                  minWidth: 200,
                  renderCell: req => req.categoryLv1?.label
                },
                {
                  label: "หมวดสินค้า Lv2",
                  minWidth: 200,
                  renderCell: req => req.categoryLv2?.label
                },
                {
                  label: "หมวดสินค้า Lv3",
                  minWidth: 200,
                  renderCell: req => req.categoryLv3?.label
                }
              ]}
              subRowRenderer={brandMapSubRowRenderer}
            />
          </Grid>

          <Grid item xs={12}>
            <SubHeader
              title={`ผูกกับสินค้า (${productMaps.length})`}
              marginTop
            />
          </Grid>
          <Grid item xs={12}>
            <SimpleSubTable<IRemyndWarrantyProductMapSaveReqTemp>
              data={productMaps}
              onClickAdd={() => setProductModal({ open: true })}
              onClickEdit={(req, idx) =>
                setProductModal({
                  open: true,
                  index: idx,
                  data: req
                })
              }
              onClickDelete={(_, idx) => delProductMap(idx)}
              showIndex={false}
              columns={[
                {
                  label: "สินค้า",
                  renderCell: ProductSubTableCell
                }
              ]}
              subRowRenderer={productMapSubRowRenderer}
            />
          </Grid>

          <Grid item xs={12}>
            <Box mt={2} mb={1}>
              <Divider light />
            </Box>
          </Grid>

          <Grid item className={classes.layoutButton} />

          <Grid item className={classes.button}>
            <Button
              variant="contained"
              onClick={() => router.push(paths.index)}
              disabled={isSaving || isLoading}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item className={classes.button}>
            <Button
              variant="contained"
              color="primary"
              disabled={isSaving || isLoading}
              type="submit"
              startIcon={
                isSaving ? (
                  <CircularProgress color="secondary" size={20} />
                ) : (
                  <SaveIcon />
                )
              }
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </form>

      {brandCatModal.open && (
        <WarrantyBrandCatModal
          label="ผูกกับแบรนด์และหมวดสินค้า"
          open={brandCatModal.open}
          defaultValue={brandCatModal.data}
          existingValues={brandCatMaps}
          handleCloseModal={handleCloseBrandCatModal}
          handleSubmitModal={handleSubmitBrandCatModal}
        />
      )}

      {productModal.open && (
        <WarrantyProductModal
          label="ผูกกับสินค้า"
          open={productModal.open}
          defaultValue={productModal.data}
          existingValues={productMaps}
          handleCloseModal={handleCloseProductModal}
          handleSubmitModal={handleSubmitProductModal}
        />
      )}

      {detailModal.open && (
        <WarrantyDetailModal
          label="รายละเอียดการรับประกัน"
          open={detailModal.open}
          defaultValue={detailModal.data}
          handleCloseModal={handleCloseDetailModal}
          handleSubmitModal={handleSubmitDetailModal}
        />
      )}
    </>
  );
};

export default Form;
